import { createTokenHeader } from '../utils/functions/headers';
import {
  TAddBulkDiscountData,
  TAddComboOfferData,
  TBulkDiscount,
  TComboOffer,
  TExportDiscountParams,
  TImportDiscountResponse,
  TProduct,
  TSkuDiscount,
  TUploadDocumentResponse,
  TgetDiscountsParams,
} from '../utils/types';
import { emptySplitApi } from './emptySplitApi';

export const discountsService = emptySplitApi
  .enhanceEndpoints({
    addTagTypes: ['SKUDiscountList', 'BulkDiscountList'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getAllSkuDiscounts: builder.query<
        { data: TSkuDiscount[]; totalCount: number },
        TgetDiscountsParams
      >({
        query: (params) => ({
          method: 'GET',
          url: 'discounts',
          params,
        }),
        transformResponse: (
          data: TSkuDiscount[],
          meta: Response
        ) => {
          const totalCount =
            meta.headers.get('X-total-count');
          return {
            data,
            totalCount: Number(totalCount ? totalCount : 0),
          };
        },
        providesTags: ['SKUDiscountList'],
      }),
      updateSkuDiscountDetails: builder.mutation<
        void,
        TSkuDiscount
      >({
        query: (data) => {
          return {
            url: 'discounts',
            method: 'PUT',
            data,
          };
        },
        invalidatesTags: ['SKUDiscountList'],
      }),
      deleteSku: builder.mutation<void, number>({
        query: (id) => {
          return {
            url: `discounts/${id}`,
            method: 'DELETE',
          };
        },
        invalidatesTags: ['SKUDiscountList'],
      }),
      getAllBulkDiscounts: builder.query<
        { data: TBulkDiscount[]; totalCount: number },
        TgetDiscountsParams
      >({
        query: (params) => ({
          method: 'GET',
          url: 'bulk-discounts',
          params: {
            ...params,
            werks: params.plant,
          },
        }),
        transformResponse: (
          data: TBulkDiscount[],
          meta: Response
        ) => {
          const totalCount =
            meta.headers.get('X-total-count');
          return {
            data,
            totalCount: Number(totalCount ? totalCount : 0),
          };
        },
        providesTags: ['BulkDiscountList'],
      }),
      editBulkDiscount: builder.mutation<
        void,
        TBulkDiscount
      >({
        query: (data) => {
          return {
            url: 'bulk-discounts',
            method: 'PUT',
            data,
          };
        },
        invalidatesTags: ['BulkDiscountList'],
      }),
      deleteBulkDiscount: builder.mutation<void, number>({
        query: (id) => {
          return {
            url: `bulk-discounts/${id}`,
            method: 'DELETE',
          };
        },
        invalidatesTags: ['BulkDiscountList'],
      }),
      importSkuDiscount: builder.mutation<
        TImportDiscountResponse,
        FormData
      >({
        query: (data) => {
          return {
            url: 'bulk/import?importType=SKU_DISCOUNT',
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
        invalidatesTags: ['SKUDiscountList'],
      }),
      importBulkDiscount: builder.mutation<
        TImportDiscountResponse,
        FormData
      >({
        query: (data) => {
          return {
            url: 'bulk/import?importType=BULK_DISCOUNT',
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
        invalidatesTags: ['BulkDiscountList'],
      }),
      importComboDiscount: builder.mutation<
        TImportDiscountResponse,
        FormData
      >({
        query: (data) => {
          return {
            url: 'combo-offer/import',
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
      }),
      exportDiscount: builder.mutation<
        TImportDiscountResponse,
        TExportDiscountParams
      >({
        query: ({ data, exportType }) => {
          return {
            url: `bulk/export?exportType=${exportType}`,
            method: 'POST',
            data,
          };
        },
      }),
      deleteBulkMultiple: builder.mutation<
        void,
        TExportDiscountParams
      >({
        query: ({ data, exportType }) => {
          return {
            url: `bulk/delete?type=${exportType}`,
            method: 'POST',
            data,
          };
        },
        invalidatesTags: ['BulkDiscountList'],
      }),
      getSkuDiscountDetails: builder.query<
        TSkuDiscount,
        string
      >({
        query: (id) => ({
          method: 'GET',
          url: `discounts/${id}`,
        }),
      }),
      getBulkDiscountDetails: builder.query<
        TBulkDiscount,
        string
      >({
        query: (id) => ({
          method: 'GET',
          url: `bulk-discounts/${id}`,
        }),
      }),
      addBulkDiscount: builder.mutation<
        void,
        TAddBulkDiscountData
      >({
        query: (data) => {
          return {
            url: 'bulk-discounts',
            method: 'POST',
            data,
          };
        },
        invalidatesTags: ['BulkDiscountList'],
      }),
      getComboDetailsById: builder.query<
        TComboOffer,
        string
      >({
        query: (id) => ({
          method: 'GET',
          url: `discounts/combo/${id}`,
        }),
      }),
      addComboOffer: builder.mutation<
        TComboOffer[],
        TAddComboOfferData
      >({
        query: (data) => {
          return {
            url: 'discounts/combo',
            method: 'POST',
            data,
          };
        },
      }),
      editComboOffer: builder.mutation<void, TSkuDiscount>({
        query: (data) => {
          return {
            url: 'discounts',
            method: 'PUT',
            data,
          };
        },
      }),
      uploadComboOfferImage: builder.mutation<
        TUploadDocumentResponse,
        { data: FormData; comboId: number }
      >({
        query: ({ data, comboId }) => {
          return {
            url: `product/${comboId}/sequence/1?productType=COMBO`,
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
      }),
      getProductList: builder.query<TProduct[], void>({
        query: () => ({
          method: 'GET',
          url: 'products/active',
        }),
      }),
    }),
  });

export const {
  useGetAllSkuDiscountsQuery,
  useUpdateSkuDiscountDetailsMutation,
  useDeleteSkuMutation,
  useGetAllBulkDiscountsQuery,
  useEditBulkDiscountMutation,
  useDeleteBulkDiscountMutation,
  useImportSkuDiscountMutation,
  useImportBulkDiscountMutation,
  useImportComboDiscountMutation,
  useExportDiscountMutation,
  useDeleteBulkMultipleMutation,
  useGetSkuDiscountDetailsQuery,
  useGetBulkDiscountDetailsQuery,
  useAddBulkDiscountMutation,
  useGetComboDetailsByIdQuery,
  useAddComboOfferMutation,
  useEditComboOfferMutation,
  useUploadComboOfferImageMutation,
  useGetProductListQuery,
} = discountsService;
